// SOURCE MENTIONS ZONE — where a public source names this organisation (BUILD-BRIEF-R6 §5.3), on
// OrganisationPage between the Network zone and `<ProfileZone>`.
//
// Two sheets, both read-only mirrors of what a third party PRINTED — never a claim the record
// holds (no `<Att>` anywhere here, same rule as the Series zone): A, one row per `source_entries`
// row naming this organisation (a réseau in a FAJE list, a recipient in a Lausanne brochure); B,
// the series points whose `geo_level` is 'organisation' and whose `geo_code` is this
// organisation's own ref (a réseau's FTE or places history). Both toggle their evidence open —
// the row as printed, the file it came from.
//
// EMPTY → THE ZONE IS NOT RENDERED (§5.3): a 404, a 501, or an answer that names nothing here
// leaves this component silent, never an error banner and never an empty card — the operator's
// page is not obliged to announce every zone that has nothing to say about THIS organisation.
//
// `GET /api/sources/entries` and the document lookup both go through the Sources package's own
// published client (`api/sources.ts`, `api/series.ts`) — this zone reads them, it does not
// re-implement or widen them.
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Fig, Lnk, Sheet, Tbl, Zone, ZoneHead } from './PageKit.js';
import { getOrganisation } from '../api/organisations.js';
import { listDocuments, listEntries } from '../api/sources.js';
import { listSeries } from '../api/series.js';
import { useLocale, useT } from '../i18n/index.js';
import { seriesLabel } from '../../shared/series.v1.js';
import type { SeriesPoint } from '../../shared/series.v1.js';
import type { SourceDocument, SourceEntry } from '../../shared/sources.v1.js';
import './SourceMentionsZone.de.js';
import './SourceMentionsZone.fr.js';

export interface SourceMentionsZoneProps {
  organisationId: number;
}

export default function SourceMentionsZone({ organisationId }: SourceMentionsZoneProps) {
  const t = useT();
  const { lang, fmtDate } = useLocale();

  const [entries, setEntries] = useState<SourceEntry[] | null>(null);
  const [openEntry, setOpenEntry] = useState<number | null>(null);
  // The entries' own documents, one `listDocuments(key)` per distinct source — an organisation
  // is rarely named by more than one or two sources, so this stays a handful of reads, never one
  // per row.
  const [docs, setDocs] = useState<Map<number, SourceDocument>>(new Map());

  const [ref, setRef] = useState<string | null>(null);
  const [points, setPoints] = useState<SeriesPoint[] | null>(null);
  const [openPoint, setOpenPoint] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    listEntries({ organisation_id: organisationId })
      .then((r) => { if (live) setEntries(r.items); })
      .catch(() => { if (live) setEntries([]); }); // 404/501: silent, never an error banner (§5.3)
    return () => { live = false; };
  }, [organisationId]);

  useEffect(() => {
    if (!entries?.length) return;
    const keys = [...new Set(entries.map((e) => e.source_key))];
    let live = true;
    Promise.all(keys.map((k) => listDocuments(k).then((r) => r.items).catch(() => [] as SourceDocument[])))
      .then((lists) => {
        if (!live) return;
        const map = new Map<number, SourceDocument>();
        for (const list of lists) for (const d of list) map.set(d.id, d);
        setDocs(map);
      });
    return () => { live = false; };
  }, [entries]);

  // The series store keys an organisation point by REF, not by id (§2.1) — one small read to
  // learn it, so OrganisationPage's own one-line change need pass nothing but the id.
  useEffect(() => {
    let live = true;
    getOrganisation(organisationId)
      .then((o) => { if (live) setRef(o.organisation.ref); })
      .catch(() => { if (live) setRef(null); });
    return () => { live = false; };
  }, [organisationId]);

  useEffect(() => {
    if (!ref) { setPoints((cur) => cur ?? []); return; }
    let live = true;
    listSeries({ geo_level: 'organisation', geo_code: ref })
      .then((r) => { if (live) setPoints(r.items); })
      .catch(() => { if (live) setPoints([]); });
    return () => { live = false; };
  }, [ref]);

  const pointDocKeys = useMemo(() => [...new Set((points ?? []).map((p) => p.source_key))], [points]);
  const [pointDocs, setPointDocs] = useState<Map<number, SourceDocument>>(new Map());
  useEffect(() => {
    if (!pointDocKeys.length) return;
    let live = true;
    Promise.all(pointDocKeys.map((k) => listDocuments(k).then((r) => r.items).catch(() => [] as SourceDocument[])))
      .then((lists) => {
        if (!live) return;
        const map = new Map<number, SourceDocument>();
        for (const list of lists) for (const d of list) map.set(d.id, d);
        setPointDocs(map);
      });
    return () => { live = false; };
  }, [pointDocKeys]);

  const loaded = entries !== null && points !== null;
  const hasEntries = !!entries?.length;
  const hasPoints = !!points?.length;
  if (!loaded) return null;
  if (!hasEntries && !hasPoints) return null; // nothing here about THIS organisation — silent

  return (
    <Zone id="source-mentions">
      <ZoneHead label={t('Named in sources')} count={<Fig>{(entries?.length ?? 0) + (points?.length ?? 0)}</Fig>} />

      {hasEntries && (
        <Sheet clip>
          <div style={{ overflowX: 'auto' }}>
            <Tbl>
              <thead>
                <tr>
                  <th>{t('Name')}</th>
                  <th>{t('Source')}</th>
                  <th>{t('Category')}</th>
                  <th>{t('Document')}</th>
                  <th className="num">{t('Last modified')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {entries!.map((e) => {
                  const doc = docs.get(e.document_id);
                  return (
                    <Fragment key={e.id}>
                      <tr>
                        <td className="voice-record">{e.name}</td>
                        <td>{e.source_key}</td>
                        <td>{e.category ?? <span className="fv--unknown">{t('not collected')}</span>}</td>
                        <td>
                          {doc?.snapshot_id != null
                            ? <Lnk href={`/api/snapshots/${doc.snapshot_id}/file`}>{doc.filename ?? t('Open')}</Lnk>
                            : <span className="fv--unknown">{t('not collected')}</span>}
                        </td>
                        <td className="num">
                          {doc?.last_modified
                            ? <Fig>{fmtDate(doc.last_modified)}</Fig>
                            : <span className="fv--unknown">{t('not collected')}</span>}
                        </td>
                        <td className="num">
                          {e.excerpt && (
                            <Lnk onClick={() => setOpenEntry((cur) => (cur === e.id ? null : e.id))}>
                              {openEntry === e.id ? t('hide') : t('Show')}
                            </Lnk>
                          )}
                        </td>
                      </tr>
                      {openEntry === e.id && e.excerpt && (
                        <tr>
                          <td colSpan={6}>
                            <blockquote className="ev">{e.excerpt}</blockquote>
                            <div className="ev-src">
                              {doc?.filename ?? e.source_key}
                              {doc?.last_modified && <> · <Fig>{fmtDate(doc.last_modified)}</Fig></>}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </Tbl>
          </div>
        </Sheet>
      )}

      {hasPoints && (
        <Sheet clip>
          <div style={{ overflowX: 'auto' }}>
            <Tbl>
              <thead>
                <tr>
                  <th>{t('Series')}</th>
                  <th className="num">{t('Period')}</th>
                  <th className="num">{t('Value')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {points!.map((p) => {
                  const doc = pointDocs.get(p.document_id);
                  return (
                    <Fragment key={p.id}>
                      <tr>
                        <td>{seriesLabel(lang, p.series_key)}</td>
                        <td className="num">{p.period}</td>
                        <td className="num">
                          <Fig className={p.value === 0 ? 'fv--nil' : undefined}>{p.value}</Fig>
                        </td>
                        <td className="num">
                          <Lnk onClick={() => setOpenPoint((cur) => (cur === p.id ? null : p.id))}>
                            {openPoint === p.id ? t('hide') : t('Show')}
                          </Lnk>
                        </td>
                      </tr>
                      {openPoint === p.id && (
                        <tr>
                          <td colSpan={4}>
                            <blockquote className="ev">{p.quote}</blockquote>
                            <div className="ev-src">
                              {doc?.filename ?? p.source_key}
                              {(doc?.last_modified ?? p.as_of) && <> · <Fig>{fmtDate(doc?.last_modified ?? p.as_of)}</Fig></>}
                              {doc?.snapshot_id != null && (
                                <> · <Lnk href={`/api/snapshots/${doc.snapshot_id}/file`}>{t('Open')}</Lnk></>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </Tbl>
          </div>
        </Sheet>
      )}
    </Zone>
  );
}
